import { MouseEvent, ReactElement, useState } from 'react'
import { ArrowDropUp, CheckCircle, Close } from '@mui/icons-material'
import { Box, Button, LinearProgress, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import { ToggleButton } from '@ui/actions'
import { Toolbar as EditorToolbar, ToolbarGroup } from '@editorComponents'
import { GuidedIFCStep } from './types'

interface Props {
  steps: GuidedIFCStep[]
  activeStep: number
  onStepChange: (index: number) => void
  onExit: () => void
}

const stepLabel = (step: GuidedIFCStep | undefined, index: number): string =>
  step?.label ?? `Schritt ${index + 1}`

/**
 * Compact guided-mode toolbar (portaled into the scene toolbar slot): the current step name with a
 * step-jump drop-up, the step counter and an exit button. (The per-step info toggle lives in the
 * step header.)
 */
const GuidedIFCToolbar = ({ steps, activeStep, onStepChange, onExit }: Props): ReactElement => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const openMenu = (event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)
  const closeMenu = () => setMenuAnchor(null)

  const jumpTo = (index: number) => {
    onStepChange(index)
    closeMenu()
  }

  const progress = steps.length ? ((activeStep + 1) / steps.length) * 100 : 0

  // Reserve the width of the longest step label so the toolbar keeps a fixed width across steps.
  const longestLabel = steps.reduce((longest, step, index) => {
    const label = stepLabel(step, index)
    return label.length > longest.length ? label : longest
  }, '')

  return (
    <EditorToolbar
      sx={{ pointerEvents: 'none' }}
      footer={
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 3 }}
          data-cy="guided-ifc-progress"
        />
      }
    >
      <ToolbarGroup>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          {/* Invisible sizer: reserves the width of the longest label. */}
          <Button
            size="small"
            color="inherit"
            endIcon={<ArrowDropUp />}
            aria-hidden
            tabIndex={-1}
            sx={{ visibility: 'hidden', textTransform: 'none', pointerEvents: 'none' }}
          >
            {longestLabel}
          </Button>
          <Tooltip title="Klick um direkt zu einem Schritt zu springen" placement="top">
            <Button
              size="small"
              color="inherit"
              endIcon={<ArrowDropUp />}
              onClick={openMenu}
              data-cy="guided-ifc-step-menu"
              sx={{
                position: 'absolute',
                inset: 0,
                justifyContent: 'space-between',
                textTransform: 'none',
              }}
            >
              {stepLabel(steps[activeStep], activeStep)}
            </Button>
          </Tooltip>
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {steps.map((step, index) => (
            <MenuItem
              key={index}
              selected={index === activeStep}
              disabled={step.skipped}
              onClick={() => jumpTo(index)}
              data-cy={`guided-ifc-step-menu-item-${index}`}
              sx={{ minHeight: 'auto' }}
            >
              {`${index + 1}. ${stepLabel(step, index)}`}
              {step.completed && <CheckCircle color="success" sx={{ fontSize: 14, ml: 0.5 }} />}
            </MenuItem>
          ))}
        </Menu>
      </ToolbarGroup>

      <ToolbarGroup>
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" data-cy="guided-ifc-counter">
            {`${activeStep + 1}/${steps.length}`}
          </Typography>
        </Box>
      </ToolbarGroup>

      <ToolbarGroup>
        <ToggleButton
          hint="Geführten Modus verlassen"
          tooltipPlacement="top"
          Icon={Close}
          onClick={onExit}
          data-cy="guided-ifc-exit"
        />
      </ToolbarGroup>
    </EditorToolbar>
  )
}

export default GuidedIFCToolbar
